import { useMemo } from 'react';
import { Size } from '@Components/designConstants';
import { Dropdown } from '../Dropdown';
import {
  CustomModalEditWidgetHeader,
  SelectorOption,
  SelectorOptions
} from './modal-edit-constants';

// Matches .dropdown-rs__menu-list's max-height: a menu that can't open close to full height below
// its field flips upward rather than being squeezed against the bottom of the viewport.
const MODAL_MIN_MENU_HEIGHT = 300;

interface SelectorElementProps {
  header: CustomModalEditWidgetHeader;
  row: any;
  value: string | number | (string | number)[] | null | undefined;
  setValue: (value: string | number | (string | number)[] | null) => void;
  selectorOptions: SelectorOptions;
  isValidNewOption?: (inputValue: string) => boolean;
  // Where searchable menus render, fixed-positioned. ModalEditor passes its modal body: an inline
  // menu near the bottom would otherwise scroll the modal overlay (shifting the whole dialog), and
  // a body-level portal would lose focus to the modal's focus trap.
  menuPortalTarget?: HTMLElement | null;
}

export const SelectorElement = ({
  header,
  row,
  value,
  setValue,
  selectorOptions,
  isValidNewOption,
  menuPortalTarget
}: SelectorElementProps) => {
  const options = useMemo<SelectorOption[]>(() => {
    if (
      !header ||
      (!header.options && !header.dynamicOptions) ||
      (!selectorOptions[header.options ?? ''] &&
        !selectorOptions[header.dynamicOptions?.(row) ?? ''])
    ) {
      return [];
    }

    const headerOptionKey = header.options || header.dynamicOptions?.(row) || '';

    if (headerOptionKey === 'networkRotation') {
      if (row.network) {
        const nested = selectorOptions.networkRotation as Record<string, SelectorOption[]>;
        return nested[row.network] ?? [];
      }
      return [];
    }
    if (headerOptionKey === 'networkRotationWithoutEach') {
      if (row.network) {
        const nested = selectorOptions.networkRotationWithoutEach as Record<
          string,
          SelectorOption[]
        >;
        return nested[row.network] ?? [];
      }
      return [];
    }
    if (headerOptionKey === 'companiesConstantOnSet') {
      if (row.company == null) {
        const opts = selectorOptions.companiesConstantOnSet;
        return Array.isArray(opts) ? opts : [];
      }
      return [{ label: row.company, value: row.company }];
    }
    const opts = selectorOptions[headerOptionKey];
    return Array.isArray(opts) ? opts : [];
  }, [header, row, selectorOptions]);

  const dropdownOptions = useMemo(
    () => options.map(opt => ({ label: String(opt.label), value: String(opt.value) })),
    [options]
  );

  const isClearable = header.isClearable == null || header.isClearable;
  const isSearchable = header.searchable !== false;
  const isCreatable = isSearchable && header.creatable !== false;

  const dropdownProps = {
    options: dropdownOptions,
    clearable: isClearable
  };
  const reactSelectProps = {
    ...(menuPortalTarget && {
      menuPosition: 'fixed' as const,
      minMenuHeight: MODAL_MIN_MENU_HEIGHT
    }),
    isValidNewOption: isValidNewOption ?? (() => false)
  };

  if (header.isMulti) {
    const multiValue = Array.isArray(value) ? value.map(String) : [];
    return (
      <Dropdown
        size={Size.LG}
        {...dropdownProps}
        menuPortalTarget={menuPortalTarget}
        searchable
        creatable={isCreatable}
        multiSelect
        value={multiValue}
        onChange={newVals => setValue(newVals)}
        reactSelectProps={reactSelectProps}
      />
    );
  }

  const singleValue = value != null && !Array.isArray(value) ? String(value) : null;

  if (!isSearchable) {
    return (
      <Dropdown
        size={Size.LG}
        {...dropdownProps}
        value={singleValue ?? undefined}
        onChange={newVal => setValue(newVal)}
      />
    );
  }

  return (
    <Dropdown
      size={Size.LG}
      {...dropdownProps}
      menuPortalTarget={menuPortalTarget}
      searchable
      creatable={isCreatable}
      value={singleValue}
      onChange={newVal => setValue(newVal)}
      reactSelectProps={reactSelectProps}
    />
  );
};
